import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SPAN_CLASSES = ['large', 'small-a', 'small-b', 'wide-a', 'wide-b'];

const Collections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/collections`).then(r => r.json()),
      fetch(`${API}/products?active=true`).then(r => r.json()),
    ])
      .then(([colData, prodData]) => {
        if (colData.success) {
          const products = prodData.success ? prodData.products : [];

          const countMap = {};
          products.forEach(p => {
            if (Array.isArray(p.collection_slugs)) {
              p.collection_slugs.forEach(slug => {
                countMap[slug] = (countMap[slug] || 0) + 1;
              });
            }
          });

          // 👇 Change is here — swap new-arrivals for bracelets
          const BENTO_SLUGS = [
            'south-indian-traditional',
            'hipchains',
            'anti-tarnish',
            'earrings',
            'natural-stones',
          ];

          const enriched = BENTO_SLUGS
            .map(slug => {
              const col = colData.collections.find(c => c.slug === slug);
              if (!col) return null;
              return { ...col, productCount: countMap[slug] || 0 };
            })
            .filter(Boolean);

          setCollections(enriched);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        .col-section {
          background: #F0EDE6;
          padding: 90px 0 80px;
        }

        /* ── Header ── */
        .col-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding: 0 clamp(24px, 5vw, 80px);
          margin-bottom: 48px;
          flex-wrap: wrap;
          gap: 20px;
        }
        .col-label {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .col-label::before {
          content: '';
          width: 28px; height: 1px;
          background: #B08D57;
          display: inline-block;
        }
        .col-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(30px, 4vw, 48px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.08;
          letter-spacing: -0.01em;
        }
        .col-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .col-viewall {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #fff;
          background: #1C1A18;
          text-decoration: none;
          padding: 14px 32px;
          display: inline-block;
          transition: background 0.22s;
          white-space: nowrap;
        }
        .col-viewall:hover { background: #B08D57; }

        /* ── Bento grid ── */
        .col-bento {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          grid-template-rows: 360px 300px;
          gap: 12px;
          padding: 0 clamp(24px, 5vw, 80px);
        }

        .col-card.large   { grid-column: span 5; grid-row: span 2; }
        .col-card.small-a { grid-column: span 4; grid-row: span 1; }
        .col-card.small-b { grid-column: span 3; grid-row: span 1; }
        .col-card.wide-a  { grid-column: span 4; grid-row: span 1; }
        .col-card.wide-b  { grid-column: span 3; grid-row: span 1; }

        /* ── Card ── */
        .col-card {
          position: relative;
          overflow: hidden;
          cursor: pointer;
          display: block;
          text-decoration: none;
        }
        .col-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.7s ease;
          display: block;
        }
        .col-card-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #2a2620, #1C1A18);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .col-card:hover img { transform: scale(1.06); }

        .col-card::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(28,26,24,0.75) 0%,
            rgba(28,26,24,0.12) 50%,
            transparent 100%
          );
          z-index: 1;
          transition: background 0.3s;
        }
        .col-card:hover::after {
          background: linear-gradient(
            to top,
            rgba(28,26,24,0.85) 0%,
            rgba(28,26,24,0.25) 60%,
            transparent 100%
          );
        }

        /* card content */
        .col-card-content {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          z-index: 2;
          padding: 24px 26px;
        }

        /* product count pill */
        .col-count {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #D4AF78;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .col-count-dot {
          width: 4px;
          height: 4px;
          background: #B08D57;
          border-radius: 50%;
          display: inline-block;
        }

        .col-name {
          font-family: 'Playfair Display', serif;
          font-size: clamp(16px, 2vw, 22px);
          font-weight: 700;
          color: #fff;
          line-height: 1.2;
          margin-bottom: 6px;
        }
        .col-desc {
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 300;
          color: rgba(255,255,255,0.7);
          letter-spacing: 0.04em;
          line-height: 1.5;
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease, opacity 0.3s;
          opacity: 0;
        }
        .col-card:hover .col-desc {
          max-height: 60px;
          opacity: 1;
        }
        .col-shop-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #D4AF78;
          text-decoration: none;
          margin-top: 10px;
          border-bottom: 1px solid rgba(212,175,120,0.4);
          padding-bottom: 2px;
          opacity: 0;
          transform: translateY(6px);
          transition: opacity 0.3s 0.05s, transform 0.3s 0.05s, border-color 0.2s;
        }
        .col-card:hover .col-shop-link {
          opacity: 1;
          transform: translateY(0);
        }
        .col-shop-link:hover { border-color: #D4AF78; color: #fff; }

        /* loading / skeleton */
        .col-loading {
          text-align: center;
          padding: 80px;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: #9A9186;
          letter-spacing: 0.1em;
        }
        .col-skeleton {
          background: linear-gradient(90deg, #e8e2d9 25%, #f0ebe3 50%, #e8e2d9 75%);
          background-size: 200% 100%;
          animation: col-shimmer 1.4s infinite;
        }
        @keyframes col-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* ── Mobile ── */
        @media (max-width: 900px) {
          .col-bento {
            grid-template-columns: 1fr 1fr;
            grid-template-rows: auto;
          }
          .col-card.large,
          .col-card.small-a,
          .col-card.small-b,
          .col-card.wide-a,
          .col-card.wide-b {
            grid-column: span 1;
            grid-row: span 1;
            height: 240px;
          }
          .col-card.large {
            grid-column: span 2;
            height: 280px;
          }
        }
        @media (max-width: 480px) {
          .col-bento { grid-template-columns: 1fr; gap: 10px; }
          .col-card.large,
          .col-card.small-a,
          .col-card.small-b,
          .col-card.wide-a,
          .col-card.wide-b {
            grid-column: span 1;
            height: 220px;
          }
          .col-section { padding: 60px 0 52px; }
        }
      `}</style>

      <section className="col-section">

        {/* Header */}
        <div className="col-header">
          <div>
            <p className="col-label">Explore</p>
            <h2 className="col-title">Our <em>Collections</em></h2>
          </div>
          <Link to="/collections" className="col-viewall">View All Collections</Link>
        </div>

        {/* Bento grid */}
        {loading ? (
          <div className="col-bento">
            {SPAN_CLASSES.map((cls, i) => (
              <div key={i} className={`col-card ${cls} col-skeleton`} />
            ))}
          </div>
        ) : collections.length === 0 ? (
          <div className="col-loading">No collections yet.</div>
        ) : (
          <div className="col-bento">
            {collections.map((col, i) => (
              <motion.div
                key={col.id}
                className={`col-card ${SPAN_CLASSES[i]}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, amount: 0.2 }}
              >
                {/* Entire card is a link to the collection */}
                <Link
                  to={`/collections/${col.slug}`}
                  style={{ display: 'block', width: '100%', height: '100%', position: 'absolute', inset: 0, zIndex: 3 }}
                  aria-label={col.name}
                />

                {/* Image or placeholder */}
                {col.image_url
                  ? <img src={col.image_url} alt={col.name} loading="lazy" />
                  : <div className="col-card-placeholder" />
                }

                {/* Content */}
                <div className="col-card-content">
                  <div className="col-count">
                    <span className="col-count-dot" />
                    {col.productCount} {col.productCount === 1 ? 'piece' : 'pieces'}
                  </div>
                  <div className="col-name">{col.name}</div>
                  {col.sub && <div className="col-desc">{col.sub}</div>}
                  <span className="col-shop-link">
                    Shop Now →
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}

      </section>
    </>
  );
};

export default Collections;